import React from 'react';
import FundingSequenceItemCard from './FundingSequenceItemCard';

const PHASE_LABELS = {
  'Phase 1': 'Highest probability / relationship',
  'Phase 2': 'Secondary expansion',
  'Phase 3': 'Alternative / backup options',
};

const FundingSequencePhaseColumn = ({ phase, items, onStatusChange, onMovePhase, onEditNotes, onCreateSubmission, creatingSubmissionId, saving }) => (
  <div className="min-w-[300px] flex-1 rounded-2xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4">
    <div className="mb-4">
      <div className="text-sm font-semibold text-[var(--fg-text)]">{phase}</div>
      <div className="text-xs text-[var(--fg-muted)]">{PHASE_LABELS[phase]}</div>
      <div className="mt-1 text-xs font-medium text-[var(--fg-accent)]">{items.length} items</div>
    </div>
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-[var(--fg-muted)]">No applications in this phase.</p>
      ) : (
        items.map((item) => (
          <FundingSequenceItemCard
            key={item.id}
            item={item}
            onStatusChange={onStatusChange}
            onMovePhase={onMovePhase}
            onEditNotes={onEditNotes}
            onCreateSubmission={onCreateSubmission}
            creatingSubmission={creatingSubmissionId === item.id}
            disabled={saving}
          />
        ))
      )}
    </div>
  </div>
);

const FundingSequenceBoard = ({ sequence, onStatusChange, onMovePhase, onEditNotes, onCreateSubmission, creatingSubmissionId, saving }) => {
  if (!sequence) {
    return (
      <p className="text-sm text-[var(--fg-muted)]">
        Generate or create a sequence to plan the recommended application order.
      </p>
    );
  }

  const items = sequence.items || [];
  const byPhase = {
    'Phase 1': items.filter((item) => item.phase === 'Phase 1').sort((a, b) => a.orderIndex - b.orderIndex),
    'Phase 2': items.filter((item) => item.phase === 'Phase 2').sort((a, b) => a.orderIndex - b.orderIndex),
    'Phase 3': items.filter((item) => item.phase === 'Phase 3').sort((a, b) => a.orderIndex - b.orderIndex),
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[var(--fg-line)] bg-white px-4 py-3 dark:bg-slate-950/20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-[var(--fg-text)]">{sequence.name}</div>
            <div className="text-sm text-[var(--fg-muted)]">
              {sequence.strategyType} · {sequence.status} · {items.length} applications
            </div>
          </div>
          {sequence.goalAmount != null ? (
            <div className="text-sm font-medium text-[var(--fg-accent)]">
              Goal {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(sequence.goalAmount)}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-2">
        {['Phase 1', 'Phase 2', 'Phase 3'].map((phase) => (
          <FundingSequencePhaseColumn
            key={phase}
            phase={phase}
            items={byPhase[phase]}
            onStatusChange={onStatusChange}
            onMovePhase={onMovePhase}
            onEditNotes={onEditNotes}
            onCreateSubmission={onCreateSubmission}
            creatingSubmissionId={creatingSubmissionId}
            saving={saving}
          />
        ))}
      </div>
    </div>
  );
};

export default FundingSequenceBoard;
